import type {
  AudioDegradationPresetId,
  VoicePersonaConfig,
} from "@/types/ModelConfigs/VoiceModelConfigs"

interface CallEnvironmentOption<TValue extends string> {
  value: TValue
  label: string
  description: string
  previewUrl?: string
}

/** Sentinel for "no preset" in the pickers, since Select-style values must be strings. */
export const CALL_ENVIRONMENT_UNSET_VALUE = "__unset__"

export const SITUATE_SPEAKER_BACKGROUND_SOUND_NOTE =
  "Point-source background sounds are situated elsewhere in the same space as the situated speaker. Ambient background sounds are mixed in directly."

export const AUDIO_DEGRADATION_EXCLUSIVITY_NOTE =
  "Cannot be combined with Situate Speaker."

type SituateSpeakerOptionValue =
  | typeof CALL_ENVIRONMENT_UNSET_VALUE
  | NonNullable<VoicePersonaConfig["situate_speaker"]>

export const SITUATE_SPEAKER_OPTIONS: Array<
  CallEnvironmentOption<SituateSpeakerOptionValue>
> = [
  {
    value: CALL_ENVIRONMENT_UNSET_VALUE,
    label: "Clean speech",
    description: "For testing a user speaking directly into a microphone.",
    previewUrl: "/audio/situate-speaker/clean-speech.wav",
  },
  {
    value: "speakerphone-easy",
    label: "Speakerphone, easy",
    description: "For testing a user speaking from a distance.",
    previewUrl: "/audio/situate-speaker/speakerphone-easy.wav",
  },
  {
    value: "speakerphone-hard",
    label: "Speakerphone, hard",
    description:
      "For testing a user speaking from a distance in a challenging acoustic environment.",
    previewUrl: "/audio/situate-speaker/speakerphone-hard.wav",
  },
]

type AudioDegradationOptionValue =
  | typeof CALL_ENVIRONMENT_UNSET_VALUE
  | AudioDegradationPresetId

export const AUDIO_DEGRADATION_OPTIONS: Array<
  CallEnvironmentOption<AudioDegradationOptionValue>
> = [
  {
    value: CALL_ENVIRONMENT_UNSET_VALUE,
    label: "Clean line",
    description:
      "No line degradation — the full range of the voice comes through.",
    previewUrl: "/audio/audio-degradation/clean.wav",
  },
  {
    value: "landline",
    label: "Landline",
    description:
      "An older landline connection — less clear than a clean line, but without dropouts.",
    previewUrl: "/audio/audio-degradation/landline.wav",
  },
  {
    value: "cell-poor",
    label: "Weak cell signal",
    description: "A weak mobile connection with occasional brief dropouts.",
    previewUrl: "/audio/audio-degradation/cell-poor.wav",
  },
  {
    value: "cell-handoff",
    label: "Cell handoff",
    description:
      "A mobile call breaking up with longer, more frequent dropouts.",
    previewUrl: "/audio/audio-degradation/cell-handoff.wav",
  },
]
